// @ts-check
/**
 * projectHierarchy.js - main project / sub-project / PO line, the way Sage
 * Intacct lists them (Sharif, 10/14/26: "Some projects, such as FANUC
 * projects, have multiple lines in their PO, so we have to issue new POs ...
 * make it look like Sage, where it's easy to identify the main projects and
 * their children").
 *
 * The links are set BY HAND only (Sharif, same day: "NO automatic grouping
 * ... I just need the set up to exist" - Maryam groups the projects): a
 * record's projects.parent_id names the record it sits under. Nothing is
 * worked out from the Global Project Number; with no parent_id anywhere the
 * tables are flat, as before.
 *
 *   main project  a record others sit under, with no parent of its own
 *   sub-project   a record directly under the main one; its own children
 *                 (and theirs, at any depth) are its PO lines
 *   PO line       every record stays one GlobalOps record
 *
 * Every record stays one row: the group rows are drawn over the records and
 * their figures are sums of the records under them, so a total, a count or
 * an export over the records never counts anything twice. A sub-project with
 * no lines of its own is just its row. PURE throughout.
 */
import { leftoverNum } from '../utils/projectMoney';
import { isProjectRow, isClosedProject } from './projectStatus';

export const gpnOf = (r) => String((r && (r.globalProjectNumber ?? r.global_project_number)) || '').trim();
export const parentIdOf = (r) => {
  const v = r && (r.parentId ?? r.parent_id);
  return v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim();
};
const nameOf = (r) => String((r && (r.name || r.description)) || '').trim();

/**
 * The chain from a record up to its main project: [main, ..., record]. A
 * parent that is not among `byId`, or a loop, ends the chain there.
 */
function chainOf(r, byId) {
  const chain = [r];
  const seen = new Set([String(r.id)]);
  let at = r;
  for (let i = 0; i < 50; i++) {
    const pid = parentIdOf(at);
    if (!pid || seen.has(pid) || !byId.has(pid)) break;
    at = byId.get(pid);
    seen.add(pid);
    chain.unshift(at);
  }
  return chain;
}

/**
 * Where each record sits: Map(id -> { main, sub, chain }). `main` is
 * 'R:<id of the top record>'; `sub` is null for the main record itself, else
 * 'R:<id of the record directly under the main one>' that it is (or is
 * under). `lookup` (every record, every year) finds a parent outside `records`.
 */
export function placementOf(records, lookup = records) {
  const byId = new Map();
  for (const r of lookup || []) byId.set(String(r.id), r);
  for (const r of records || []) if (!byId.has(String(r.id))) byId.set(String(r.id), r);
  const map = new Map();
  for (const r of records || []) {
    const chain = chainOf(r, byId);
    map.set(String(r.id), {
      main: `R:${chain[0].id}`,
      sub: chain.length > 1 ? `R:${chain[1].id}` : null,
      chain: chain.map((x) => String(x.id)),
    });
  }
  return map;
}

/**
 * The groups over `records`: { placement, mains: Map(key -> main) }, where
 *   main = { key, root, gpn, label, mainRecords, subs: Map(key -> sub), members }
 *   sub  = { key, head, gpn, label, records }   (records: the head first)
 * `root` / `head` is the record itself (looked up in `lookup` when the table does not
 * show it - another year); `members` are every record of the main project in
 * `records`.
 * @param {any[]} records
 * @param {{ lookup?: any[] }} opts
 */
export function buildGroups(records, { lookup } = {}) {
  const look = lookup || records || [];
  const byId = new Map(look.map((r) => [String(r.id), r]));
  for (const r of records || []) if (!byId.has(String(r.id))) byId.set(String(r.id), r);
  const placement = placementOf(records, look);
  const mains = new Map();
  for (const r of records || []) {
    const at = placement.get(String(r.id));
    let m = mains.get(at.main);
    if (!m) {
      const root = byId.get(at.main.slice(2)) || r;
      m = { key: at.main, root, mainRecords: [], subs: new Map(), members: [] };
      mains.set(at.main, m);
    }
    m.members.push(r);
    if (!at.sub) {
      m.mainRecords.push(r);
      continue;
    }
    let s = m.subs.get(at.sub);
    if (!s) {
      const head = byId.get(at.sub.slice(2)) || r;
      s = { key: at.sub, head, records: [] };
      m.subs.set(at.sub, s);
    }
    s.records.push(r);
  }
  for (const m of mains.values()) {
    m.label = nameOf(m.root);
    m.gpn = gpnOf(m.root);
    for (const s of m.subs.values()) {
      s.label = nameOf(s.head);
      s.gpn = gpnOf(s.head);
      // the sub-project's own record first, then its lines
      const hid = String(s.head.id);
      s.records.sort((a, b) => Number(String(b.id) === hid) - Number(String(a.id) === hid));
    }
  }
  return { placement, mains };
}

/** The main project a record belongs to in `groups`, or null. */
export function mainOf(groups, id) {
  const at = groups && groups.placement.get(String(id));
  return at ? groups.mains.get(at.main) || null : null;
}

/** "P26006-000 · Oshkosh 3003313 Process Support" - the export's Main project column; '' for a record on its own. */
export function mainLabelOf(groups, record) {
  const m = mainOf(groups, record && record.id);
  if (!m || m.members.length < 2) return '';
  return [m.gpn, m.label].filter(Boolean).join(' · ');
}

/** True when any record has a Main project set (the tables offer grouping only then). */
export const anyLinked = (records) => (records || []).some((r) => !!parentIdOf(r));

/**
 * The tree to draw for `rows` (already filtered and sorted, the table's own
 * order), over `groups` built from the table's records. A main project shows
 * where its best-placed record is; under it its own record first, then its
 * sub-projects in the table's order. A sub-project with lines is a
 * sub-project row (its own record first, then the lines); without, its row.
 * A main project with one record is a plain row.
 * Nodes: { type: 'group', level: 'main' | 'sub', key, gpn, label, records,
 *          total, children, group, mainRecordId }
 *        { type: 'row', key, record, isMainRecord }
 * A group's `records` are the rows of `rows` under it; `total` how many
 * records it has in all (more than `records` when a filter hides some).
 * `mainRecordId` is the record the group row opens.
 */
export function treeOf(rows, groups) {
  const order = [];
  const byMain = new Map();
  for (const r of rows || []) {
    const at = groups.placement.get(String(r.id));
    const key = at ? at.main : `R:${r.id}`;
    if (!byMain.has(key)) {
      byMain.set(key, []);
      order.push(key);
    }
    byMain.get(key).push(r);
  }
  const rowNode = (r, isMainRecord = false) => ({ type: 'row', key: String(r.id), record: r, isMainRecord });
  const out = [];
  for (const key of order) {
    const shown = byMain.get(key);
    const m = groups.mains.get(key);
    if (!m || m.members.length < 2) {
      for (const r of shown) out.push(rowNode(r));
      continue;
    }
    const subOrder = [];
    const bySub = new Map();
    const mainRows = [];
    for (const r of shown) {
      const at = groups.placement.get(String(r.id));
      if (!at.sub) {
        mainRows.push(r);
        continue;
      }
      if (!bySub.has(at.sub)) {
        bySub.set(at.sub, []);
        subOrder.push(at.sub);
      }
      bySub.get(at.sub).push(r);
    }
    /** @type {any[]} rows and sub-project groups */
    const children = mainRows.map((r) => rowNode(r, true));
    for (const sk of subOrder) {
      const s = m.subs.get(sk);
      const hid = String(s.head.id);
      const lines = bySub.get(sk).sort((a, b) => Number(String(b.id) === hid) - Number(String(a.id) === hid));
      if (s.records.length < 2) {
        for (const r of lines) children.push(rowNode(r));
        continue;
      }
      children.push({
        type: 'group',
        level: 'sub',
        key: `${key}>${sk}`,
        gpn: s.gpn,
        label: s.label,
        records: lines,
        total: s.records.length,
        children: lines.map((r) => rowNode(r, String(r.id) === hid)),
        group: s,
        main: m,
        mainRecordId: hid,
      });
    }
    out.push({
      type: 'group',
      level: 'main',
      key,
      gpn: m.gpn,
      label: m.label,
      records: shown,
      total: m.members.length,
      children,
      group: m,
      main: m,
      mainRecordId: String(m.root.id),
    });
  }
  return out;
}

/**
 * The tree as the table's rows: [{ node, depth, last, open, partial }].
 * A group's children follow it when `isOpen(key)`; `partial` marks the rows
 * of a group that a filter thins out (they are the matches - the group row
 * stays for context).
 */
export function flattenTree(nodes, isOpen, depth = 0, partial = false) {
  const out = [];
  (nodes || []).forEach((node, i) => {
    const last = i === nodes.length - 1;
    if (node.type === 'row') {
      out.push({ node, depth, last, partial });
      return;
    }
    const open = !!isOpen(node.key);
    out.push({ node, depth, last, open, partial });
    if (open) out.push(...flattenTree(node.children, isOpen, depth + 1, node.records.length < node.total));
  });
  return out;
}

/** Every group key in `nodes` (Expand all). */
export function groupKeys(nodes) {
  const out = [];
  for (const n of nodes || [])
    if (n.type === 'group') {
      out.push(n.key);
      out.push(...groupKeys(n.children));
    }
  return out;
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const same = (list, f) => {
  const vals = [...new Set(list.map(f).map((v) => (v === null || v === undefined ? '' : String(v).trim())))];
  return vals.length === 1 ? vals[0] : '';
};
const pick = (r, a, b) => r[a] ?? r[b];

/**
 * A group row's figures, as a record the table's cells can draw: the money
 * summed over `records` (the leftover as the sum of each record's), the
 * dates' span, and the text the records share (blank where they differ).
 * `id` is 'group:<key>' - never a real record's. `__group` carries the level,
 * the counts and the summed leftover.
 * @param {any[]} records
 * @param {{ key?: string, level?: string, gpn?: string, label?: string, total?: any }} opts
 */
export function rollupRow(records, { key, level = 'main', gpn = '', label = '', total } = {}) {
  const list = records || [];
  const sum = (a, b) => Math.round(list.reduce((s, r) => s + num(pick(r, a, b)), 0) * 100) / 100;
  const dates = (a, b) =>
    list
      .map((r) => String(pick(r, a, b) || '').slice(0, 10))
      .filter(Boolean)
      .sort();
  const quoteDates = dates('quoteDate', 'quote_date');
  const poDates = dates('poDateIssued', 'po_date_issued');
  const invDates = dates('latestInvoiceDate', 'latest_invoice_date');
  const poBase = same(list, (r) =>
    String(pick(r, 'poNumber', 'po_number') || '')
      .replace(/\s*(&|ln).*$/i, '')
      .trim()
  );
  const engineers = [
    ...new Set(
      list.flatMap((r) =>
        String(r.engineer || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      )
    ),
  ];
  const leftover = Math.round(list.reduce((s, r) => s + leftoverNum(r), 0) * 100) / 100;
  const projects = list.filter(isProjectRow);
  return {
    id: `group:${key}`,
    name: label,
    description: label,
    globalProjectNumber: gpn,
    customer: same(list, (r) => r.customer),
    subCustomer: same(list, (r) => pick(r, 'subCustomer', 'sub_customer')),
    approvalManager: same(list, (r) => pick(r, 'approvalManager', 'approval_manager')),
    engineer: engineers.join(', '),
    pm: same(list, (r) => r.pm),
    type: same(list, (r) => r.type),
    owner: same(list, (r) => r.owner),
    currency: same(list, (r) => r.currency),
    terms: same(list, (r) => r.terms),
    quoteLetter: same(list, (r) => pick(r, 'quoteLetter', 'quote_letter')),
    rev: same(list, (r) => r.rev),
    quoteType: same(list, (r) => pick(r, 'quoteType', 'quote_type')),
    status: same(list, (r) => r.status),
    // Closed only when every record under it is
    closed: projects.length && projects.every(isClosedProject) ? 'YES' : '',
    quoteDate: quoteDates[0] || '',
    poDateIssued: poDates[0] || '',
    latestInvoiceDate: invDates[invDates.length - 1] || '',
    bookDate: '',
    billDate: '',
    poNumber: poBase,
    quotedPrice: sum('quotedPrice', 'quoted_price'),
    quotedLaborCost: sum('quotedLaborCost', 'quoted_labor_cost'),
    quotedExpenses: sum('quotedExpenses', 'quoted_expenses'),
    poValue: sum('poValue', 'po_value'),
    invoicedAlready: sum('invoicedAlready', 'invoiced_already'),
    moneyLeftover: leftover,
    probability: null,
    __group: { key, level, count: list.length, total: total === undefined ? list.length : total, leftover },
  };
}

/**
 * A record's place among `all` records (the record page):
 * { groups, main, sub, isMainRecord } - or null when the record is on its
 * own (no Main project, nothing under it).
 */
export function familyOf(all, id) {
  if (id === null || id === undefined) return null;
  const groups = buildGroups(all || []);
  const at = groups.placement.get(String(id));
  const main = at ? groups.mains.get(at.main) : null;
  if (!main || main.members.length < 2) return null;
  return { groups, main, sub: at.sub ? main.subs.get(at.sub) || null : null, isMainRecord: !at.sub };
}

/** The records whose Main project is `id` (one level). */
export function childrenOf(all, id) {
  return (all || []).filter((r) => parentIdOf(r) === String(id));
}

/** The ids of every record under `id`, at any depth - never offered as its main project. */
export function descendantIds(all, id) {
  const kids = new Map();
  for (const r of all || []) {
    const p = parentIdOf(r);
    if (!p) continue;
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(String(r.id));
  }
  const out = new Set();
  const walk = (k) => {
    for (const c of kids.get(k) || [])
      if (!out.has(c)) {
        out.add(c);
        walk(c);
      }
  };
  walk(String(id));
  return out;
}

/** The ids above `id` (its main project, and that one's, ...) - a record cannot go under itself. */
export function ancestorIds(all, id) {
  const byId = new Map((all || []).map((r) => [String(r.id), r]));
  const out = new Set();
  let at = byId.get(String(id));
  for (let i = 0; i < 50 && at; i++) {
    const p = parentIdOf(at);
    if (!p || out.has(p) || p === String(id)) break;
    out.add(p);
    at = byId.get(p);
  }
  return out;
}

/** "P26006-030 · Oshkosh Additional Paint ... · 100182313Ln2" - how a record reads in the pickers. */
export function recordLabel(r) {
  if (!r) return '';
  return [gpnOf(r), nameOf(r), String(r.poNumber || r.po_number || '').trim()].filter(Boolean).join(' · ') || String(r.id);
}

/** What the pickers search: number, name, description, PO # and customer. */
export function recordSearchText(r) {
  return [gpnOf(r), r.name, r.description, r.poNumber || r.po_number, r.customer, r.quoteLetter || r.quote_letter]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
